import { useState, useEffect, useCallback } from 'react'

// Parse markdown content into FAQ data
const parseFaqsFromMarkdown = markdown => {
  const faqs = []
  const lines = markdown.split('\n')
  let currentQuestion = null
  let currentAnswer = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Check if line is a question (starts with ###)
    if (line.startsWith('### ')) {
      // Save previous FAQ if exists
      if (currentQuestion && currentAnswer.length > 0) {
        faqs.push({
          question: currentQuestion,
          answer: currentAnswer.join('\n').trim(),
        })
      }
      // Start new FAQ
      currentQuestion = line.replace('### ', '').trim()
      currentAnswer = []
    } else if (currentQuestion && line.trim() !== '') {
      // Add to current answer
      currentAnswer.push(line)
    }
  }

  // Add last FAQ
  if (currentQuestion && currentAnswer.length > 0) {
    faqs.push({
      question: currentQuestion,
      answer: currentAnswer.join('\n').trim(),
    })
  }

  return faqs
}

// Render markdown answer content as JSX
const renderAnswer = answerText => {
  const lines = answerText.split('\n')
  const elements = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()

    if (line === '') continue

    // Handle lists
    if (line.startsWith('- ')) {
      const listItems = []
      let j = i
      while (j < lines.length && lines[j].trim().startsWith('- ')) {
        listItems.push(lines[j].trim().replace('- ', ''))
        j++
      }

      elements.push(
        <ul key={i} className="mb-4 list-inside list-disc space-y-1 pl-4">
          {listItems.map((item, idx) => {
            const processedItem = item.replace(
              /(\b(?:£|€|\$)?\d[\d,.]*(?:%|st|nd|rd|th)?\b)/g,
              '<span class="font-raleway-numbers">$1</span>'
            )
            return (
              <li
                key={idx}
                dangerouslySetInnerHTML={{ __html: processedItem }}
              />
            )
          })}
        </ul>
      )
      i = j - 1
    } else {
      // Handle regular paragraphs
      const processedLine = line
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(
          /(\b(?:£|€|\$)?\d[\d,.]*(?:%|st|nd|rd|th)?\b)/g,
          '<span class="font-raleway-numbers">$1</span>'
        )
        .replace(/&apos;/g, "'")
        .replace(/&quot;/g, '"')

      elements.push(
        <p
          key={i}
          className="mb-4"
          dangerouslySetInnerHTML={{ __html: processedLine }}
        />
      )
    }
  }

  return <div>{elements}</div>
}

// Custom hook to load FAQs from markdown file
export const useFaqs = (limit = null) => {
  const initialMarkdown = globalThis.__YCM_PRERENDER_DATA__?.faqs || ''
  const createFaqs = useCallback(
    markdown => {
      const parsedFaqs = parseFaqsFromMarkdown(markdown)
      const faqsWithJsx = parsedFaqs.map(faq => ({
        question: faq.question,
        answer: renderAnswer(faq.answer),
        answerText: faq.answer
          .replace(/\*\*(.*?)\*\*/g, '$1')
          .replace(/\s+/g, ' ')
          .trim(),
      }))

      return limit ? faqsWithJsx.slice(0, limit) : faqsWithJsx
    },
    [limit]
  )

  const [faqs, setFaqs] = useState(() => createFaqs(initialMarkdown))
  const [loading, setLoading] = useState(!initialMarkdown)
  const [error, setError] = useState(null)

  useEffect(() => {
    const loadFaqs = async () => {
      try {
        const response = await fetch('/content/faqs.md')
        if (!response.ok) {
          throw new Error('Failed to load FAQs')
        }
        const text = await response.text()
        setFaqs(createFaqs(text))
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadFaqs()
  }, [createFaqs])

  return { faqs, loading, error }
}

export default useFaqs
